import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { ADP_MARKETPLACE_URL } from 'global/appConstants';
import { endpoints } from 'global/endpoints';
import { roles } from 'global/rolesAndPermissions';
import { routePaths } from 'global/routePaths';
import { localStorageConstants } from 'global/storageConstants';
import { useUserStore } from 'store/useUserStore';
import http from 'utils/http';
import { stringifyQuery } from 'utils/query';
import * as localStorage from 'utils/storage';
import toaster from 'utils/toaster';
import token from 'utils/token';

export interface IADPLogin {
  adpLogin: boolean;
  userId: string;
  app: string;
  users: string;
  account: string;
}

type ADPLoginPayload = {
  apiToken: string;
  userUniqueToken: string;
  apiTokenExpiresAt: number;
  userInfo: {
    roleId: number;
  };
  // other fields in your payload if needed
};

type ADPLoginResponse = {
  payload: ADPLoginPayload;
};

const ADP_CORE_APP = ['RUN_CORE', 'new'];

const fetchADPloginDetails = ({ app, users, account, ...query }: IADPLogin): Promise<ADPLoginResponse> => {
  const stringifiedQuery = stringifyQuery(query);
  const newEndpoint = endpoints.adpLogin + '?' + stringifiedQuery;
  return http().get(newEndpoint);
};

export function useFetchADPloginDetails(query: IADPLogin, options: any): UseQueryResult<ADPLoginResponse, unknown> {
  const navigate = useNavigate();
  const addUserDetails = useUserStore(state => state.addUserDetails);

  const queryResult = useQuery<ADPLoginResponse, unknown>({
    queryKey: ['fetchADPloginDetails', query],
    queryFn: () => fetchADPloginDetails(query),
    ...options,
  });

  // Handle side effects when data is successfully fetched
  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      const { payload } = queryResult.data;

      if (payload) {
        const { apiToken, userUniqueToken, apiTokenExpiresAt, ...userData } = payload;

        if (apiToken && userUniqueToken && apiTokenExpiresAt && userData) {
          token.setToken({
            accessToken: apiToken,
            userUniqueToken: userUniqueToken,
            expiryDate: '' + apiTokenExpiresAt,
          });
          addUserDetails(userData);

          //Setting ADP Redirect URL for redirecting users to adp account and users section
          if (ADP_CORE_APP.includes(query.app)) {
            localStorage.set(localStorageConstants.ADP_USER_REDIRECT, query.users);
            localStorage.set(localStorageConstants.ADP_ACCOUNT_REDIRECT, query.account);
            localStorage.set(localStorageConstants.ADP_LOGOUT_REDIRECT, ADP_MARKETPLACE_URL);
          }

          if (userData?.userInfo?.roleId === roles.EMPLOYEE) {
            setTimeout(() => {
              navigate(routePaths.employeeTimeClock.route);
            }, 0);
          } else {
            setTimeout(() => {
              navigate(routePaths.dashboard);
            }, 0);
          }
        } else {
          toaster.error({ description: 'Invalid token information' });
        }
      }
    }
  }, [queryResult.isSuccess, queryResult.data, navigate, addUserDetails, query.app, query.users, query.account]);

  return queryResult;
}
